/* eslint-disable max-lines */
import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors, layout } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import { buildIS54Pdf } from '../utils/pdfBuilders';
import { useLivePdfPreview } from '../utils/livePdfPreview';
import { getPreEmploymentLookup } from '../utils/preEmploymentLookup';
import { sanitizeAmountInput } from '../utils/numberValidation';
import { BaseStepProps } from './BaseStepProps';

const IS54_PDF = 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/is-54-form.pdf';

interface IS54Draft {
  noticeGivenAtHiring: boolean;
  noticeGivenBeforeChange: boolean;
  employeeRateOfPay: string;
  allowanceNone: boolean;
  allowanceTips: boolean;
  allowanceTipsAmount: string;
  allowanceMeals: boolean;
  allowanceMealsAmount: string;
  allowanceLodging: boolean;
  allowanceLodgingAmount: string;
  allowanceOther: boolean;
  allowanceOtherDescription: string;
  regularPayday: string;
  payFrequencySelection: 'weekly' | 'biWeekly' | 'other' | '';
  ackLanguageSelection: 'english' | 'otherLanguage' | '';
  ackPrimaryLanguage: string;
  printEmployeeName: string;
  preparerNameAndTitle: string;
}

const empty: IS54Draft = {
  noticeGivenAtHiring: true,
  noticeGivenBeforeChange: false,
  employeeRateOfPay: '0',
  allowanceNone: true,
  allowanceTips: false,
  allowanceTipsAmount: '',
  allowanceMeals: false,
  allowanceMealsAmount: '',
  allowanceLodging: false,
  allowanceLodgingAmount: '',
  allowanceOther: false,
  allowanceOtherDescription: '',
  regularPayday: '',
  payFrequencySelection: '',
  ackLanguageSelection: 'english',
  ackPrimaryLanguage: '',
  printEmployeeName: '',
  preparerNameAndTitle: '',
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const radioStyle = (selected: boolean): React.CSSProperties => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? colors.accent : '#9CA3AF'}`,
  position: 'relative',
  flexShrink: 0,
});

const radioInner: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: colors.accent,
};

const IS54Step = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  application,
  applicationDraft,
  draftDocs,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const initialPay = tokenData.payRates?.[0]?.payRate ? Number(tokenData.payRates[0].payRate).toFixed(2) : '0.00';
  const backendDraft = draftDocs.find((d) => d.type === 'IS54');
  const lookup = getPreEmploymentLookup(application, applicationDraft, token);
  const initialFromBackend: IS54Draft = {
    ...empty,
    employeeRateOfPay: initialPay,
    printEmployeeName: lookup.fullName,
    ...(backendDraft?.options || {}),
  };
  const initial = (draft as IS54Draft) || initialFromBackend;
  const [data, setData] = useState<IS54Draft>({ ...empty, employeeRateOfPay: initialPay, ...initial });
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const update = (patch: Partial<IS54Draft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const overtimePayRate = (Number(data.employeeRateOfPay || 0) * 1.5).toFixed(2);

  const allowanceAmountsValid =
    (!data.allowanceTips || !!data.allowanceTipsAmount) &&
    (!data.allowanceMeals || !!data.allowanceMealsAmount) &&
    (!data.allowanceLodging || !!data.allowanceLodgingAmount) &&
    (!data.allowanceOther || !!data.allowanceOtherDescription);

  const ackLanguageValid = data.ackLanguageSelection !== 'otherLanguage' || !!data.ackPrimaryLanguage;

  const canOpenSignature =
    !!data.employeeRateOfPay &&
    !!data.regularPayday &&
    !!data.payFrequencySelection &&
    !!data.ackLanguageSelection &&
    !!data.printEmployeeName &&
    allowanceAmountsValid &&
    ackLanguageValid &&
    !savingDraft;

  const buildFormDataPayload = (signaturePng: string) => ({
    employerName: tokenData.school?.name || '',
    dbaName: tokenData.school?.name || '',
    fein: tokenData.school?.federalTaxId || '',
    physicalAddress: tokenData.school?.address || '',
    mailingAddress: tokenData.school?.address || '',
    employerPhone: tokenData.school?.phone || '',
    ...data,
    overtimePayRate,
    date: signaturePng ? moment().format('MM/DD/YYYY') : '',
    employeeSignature: signaturePng,
  });

  const livePdfUrl = useLivePdfPreview({
    builder: async () => (await buildIS54Pdf(IS54_PDF, buildFormDataPayload(''), '')) || undefined,
    deps: [
      data,
      tokenData.school?.name,
      tokenData.school?.federalTaxId,
      tokenData.school?.address,
      tokenData.school?.phone,
    ],
  });

  const submitWithSignature = async (signaturePng: string) => {
    const formData = buildFormDataPayload(signaturePng);
    const pdfBytes = await buildIS54Pdf(IS54_PDF, formData, signaturePng);
    const pdfSigned = pdfBytes ? await blobToBase64(new Blob([pdfBytes as any], { type: 'application/pdf' })) : null;

    await HireDocuments('').create({
      type: 'IS54',
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl: IS54_PDF,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: {
        ...formData,
        signPng: signaturePng,
        signatureDate: moment().format('YYYY-MM-DD'),
      },
    });

    clearDraft();
    toast.success('IS-54 signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const formData = buildFormDataPayload('');
      await HireDocuments('').create({
        type: 'IS54',
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl: IS54_PDF,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: { ...formData, signPng: '' },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save IS-54.');
    } finally {
      setSavingDraft(false);
    }
  };

  const allowanceCheckboxRow = (
    label: string,
    checked: boolean,
    onCheck: (v: boolean) => void,
    amountValue?: string,
    onAmountChange?: (v: string) => void,
    amountPlaceholder = '$0',
  ) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, flex: 1 }}>
        <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} />
        {label}
      </label>
      {onAmountChange && (
        <input
          type="text"
          value={amountValue || ''}
          onChange={(e) => onAmountChange(e.target.value)}
          placeholder={amountPlaceholder}
          disabled={!checked}
          style={{ ...layout.input, width: 140, opacity: checked ? 1 : 0.5 }}
        />
      )}
    </div>
  );

  const payFreqOptions: { value: IS54Draft['payFrequencySelection']; label: string }[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biWeekly', label: 'Bi-Weekly' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
        Form IS-54: Notice and Acknowledgement of Pay Rate and Payday
      </h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Review the form below, complete the fields and sign at the bottom.
      </p>

      <InlinePdfViewer url={livePdfUrl || IS54_PDF} maxHeight="55vh" />

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Employer Information</h4>
      <div
        style={{
          background: '#F9FAFB',
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          padding: 14,
          marginBottom: 16,
          fontSize: 13,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
          <span style={{ color: colors.muted }}>Employer:</span>
          <strong>{tokenData.school?.name || '—'}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
          <span style={{ color: colors.muted }}>FEIN:</span>
          <strong>{tokenData.school?.federalTaxId || '—'}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
          <span style={{ color: colors.muted }}>Address:</span>
          <strong>{tokenData.school?.address || '—'}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
          <span style={{ color: colors.muted }}>Phone:</span>
          <strong>{tokenData.school?.phone || '—'}</strong>
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Notice Given</h4>
      <label style={{ ...layout.optionCard, ...(data.noticeGivenAtHiring ? layout.optionCardSelected : {}) }}>
        <input
          type="checkbox"
          checked={data.noticeGivenAtHiring}
          onChange={(e) =>
            update({
              noticeGivenAtHiring: e.target.checked,
              noticeGivenBeforeChange: e.target.checked ? false : data.noticeGivenBeforeChange,
            })
          }
        />
        At hiring
      </label>
      <label style={{ ...layout.optionCard, ...(data.noticeGivenBeforeChange ? layout.optionCardSelected : {}) }}>
        <input
          type="checkbox"
          checked={data.noticeGivenBeforeChange}
          onChange={(e) =>
            update({
              noticeGivenBeforeChange: e.target.checked,
              noticeGivenAtHiring: e.target.checked ? false : data.noticeGivenAtHiring,
            })
          }
        />
        Before a change in pay rate(s), allowances claimed, or payday
      </label>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Pay</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Employee&apos;s rate of pay (per hour) *</label>
          <input
            type="text"
            inputMode="decimal"
            value={data.employeeRateOfPay}
            onChange={(e) => update({ employeeRateOfPay: sanitizeAmountInput(e.target.value) })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Overtime pay rate (1.5x — auto-calculated)</label>
          <input
            type="text"
            value={`$${overtimePayRate}`}
            disabled
            style={{ ...layout.input, background: '#E5E7EB' }}
          />
        </div>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Allowances Claimed</h4>
      {allowanceCheckboxRow('None', data.allowanceNone, (v) =>
        update({
          allowanceNone: v,
          ...(v
            ? {
                allowanceTips: false,
                allowanceMeals: false,
                allowanceLodging: false,
                allowanceOther: false,
              }
            : {}),
        }),
      )}
      {allowanceCheckboxRow(
        'Tips',
        data.allowanceTips,
        (v) => update({ allowanceTips: v, allowanceNone: v ? false : data.allowanceNone }),
        data.allowanceTipsAmount,
        (val) => update({ allowanceTipsAmount: sanitizeAmountInput(val) }),
      )}
      {allowanceCheckboxRow(
        'Meals',
        data.allowanceMeals,
        (v) => update({ allowanceMeals: v, allowanceNone: v ? false : data.allowanceNone }),
        data.allowanceMealsAmount,
        (val) => update({ allowanceMealsAmount: sanitizeAmountInput(val) }),
      )}
      {allowanceCheckboxRow(
        'Lodging',
        data.allowanceLodging,
        (v) => update({ allowanceLodging: v, allowanceNone: v ? false : data.allowanceNone }),
        data.allowanceLodgingAmount,
        (val) => update({ allowanceLodgingAmount: sanitizeAmountInput(val) }),
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, flex: 1 }}>
          <input
            type="checkbox"
            checked={data.allowanceOther}
            onChange={(e) =>
              update({ allowanceOther: e.target.checked, allowanceNone: e.target.checked ? false : data.allowanceNone })
            }
          />
          Other (describe)
        </label>
        <input
          type="text"
          value={data.allowanceOtherDescription}
          onChange={(e) => update({ allowanceOtherDescription: e.target.value })}
          placeholder="Description"
          disabled={!data.allowanceOther}
          style={{ ...layout.input, width: 220, opacity: data.allowanceOther ? 1 : 0.5 }}
        />
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Payday</h4>
      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Regular payday (e.g., every Friday) *</label>
        <input
          type="text"
          value={data.regularPayday}
          onChange={(e) => update({ regularPayday: e.target.value })}
          style={layout.input}
        />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Pay frequency *</label>
        {payFreqOptions.map((opt) => (
          <div
            key={opt.value}
            onClick={() => update({ payFrequencySelection: opt.value })}
            style={{
              ...layout.optionCard,
              ...(data.payFrequencySelection === opt.value ? layout.optionCardSelected : {}),
            }}
          >
            <div style={radioStyle(data.payFrequencySelection === opt.value)}>
              {data.payFrequencySelection === opt.value && <div style={radioInner} />}
            </div>
            {opt.label}
          </div>
        ))}
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '20px 0 10px' }}>Notice Acknowledgement</h4>
      <p style={{ color: colors.muted, fontSize: 13, marginTop: 0, marginBottom: 10 }}>
        Confirm that this notice was provided to you in your primary language.
      </p>
      {(['english', 'otherLanguage'] as const).map((opt) => (
        <div
          key={opt}
          onClick={() => update({ ackLanguageSelection: opt })}
          style={{
            ...layout.optionCard,
            ...(data.ackLanguageSelection === opt ? layout.optionCardSelected : {}),
          }}
        >
          <div style={radioStyle(data.ackLanguageSelection === opt)}>
            {data.ackLanguageSelection === opt && <div style={radioInner} />}
          </div>
          {opt === 'english' ? 'English' : 'Other (specify primary language)'}
        </div>
      ))}
      {data.ackLanguageSelection === 'otherLanguage' && (
        <div style={{ marginBottom: 16 }}>
          <label style={layout.label}>Primary language</label>
          <input
            type="text"
            value={data.ackPrimaryLanguage}
            onChange={(e) => update({ ackPrimaryLanguage: e.target.value })}
            style={layout.input}
          />
        </div>
      )}

      <div style={{ marginTop: 20, marginBottom: 16 }}>
        <label style={layout.label}>Print employee name *</label>
        <input
          type="text"
          value={data.printEmployeeName}
          onChange={(e) => update({ printEmployeeName: e.target.value })}
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Preparer name and title (if completed by someone other than the employee)</label>
        <input
          type="text"
          value={data.preparerNameAndTitle}
          onChange={(e) => update({ preparerNameAndTitle: e.target.value })}
          style={layout.input}
        />
      </div>

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title="Sign IS-54"
        submitLabel="Accept & Sign"
        previewPdfUrl={livePdfUrl}
      />
    </StepShell>
  );
};

export default IS54Step;
